import { useState, type FormEvent } from 'react';
import { useSesionOperador } from '../../servicios/sesion-operador.js';
import { ErrorApi } from '../../servicios/api.js';
import { Marca } from '../../componentes/marca.js';
import { Aviso } from '../../componentes/piezas.js';

type Paso = 'credenciales' | 'codigo' | 'inscripcion';

/**
 * Acceso a la consola de plataforma.
 *
 * El segundo factor no es opcional ni configurable: una cuenta sin TOTP no
 * inicia sesión, la inscribe. Por eso el formulario tiene tres estados en vez de
 * uno, y el de inscripción es el que ve todo operador nuevo la primera vez.
 */
export function AccesoOperador(): JSX.Element {
  const { iniciar, inscribirMfa, confirmarMfa } = useSesionOperador();

  const [paso, setPaso] = useState<Paso>('credenciales');
  const [email, setEmail] = useState('');
  const [contrasena, setContrasena] = useState('');
  const [codigo, setCodigo] = useState('');
  const [secreto, setSecreto] = useState<{ secreto: string; uri: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  function fallo(e: unknown): void {
    setError(e instanceof ErrorApi ? e.message : 'No se pudo completar la operación.');
  }

  async function enviarCredenciales(evento: FormEvent): Promise<void> {
    evento.preventDefault();
    setError(null);
    setEnviando(true);

    try {
      const desafio = await iniciar(email, contrasena);
      // La cuenta existe y la contraseña es correcta, pero no hay segundo
      // factor todavía: se pide el secreto y se pasa a inscripción.
      if (desafio) {
        setSecreto((await inscribirMfa(email, contrasena)).inscripcion);
        setPaso('inscripcion');
      }
    } catch (e) {
      if (e instanceof ErrorApi && e.codigo === 'MFA_REQUERIDA') {
        setPaso('codigo');
        setError(null);
      } else {
        fallo(e);
      }
    } finally {
      setEnviando(false);
    }
  }

  async function enviarCodigo(evento: FormEvent): Promise<void> {
    evento.preventDefault();
    setError(null);
    setEnviando(true);

    try {
      if (paso === 'inscripcion') await confirmarMfa(email, contrasena, codigo);
      else await iniciar(email, contrasena, codigo);
    } catch (e) {
      setCodigo('');
      fallo(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="acceso">
      <form
        className="acceso-tarjeta"
        onSubmit={(e) => void (paso === 'credenciales' ? enviarCredenciales(e) : enviarCodigo(e))}
      >
        <div className="acceso-marca">
          <Marca />
        </div>
        <p className="acceso-subtitulo">Consola de plataforma</p>

        {error ? <Aviso tono="error">{error}</Aviso> : null}

        {paso === 'credenciales' ? (
          <>
            <div className="campo">
              <label htmlFor="email-op">Correo</label>
              <input
                id="email-op"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                required
                autoFocus
              />
            </div>

            <div className="campo">
              <label htmlFor="contrasena-op">Contraseña</label>
              <input
                id="contrasena-op"
                type="password"
                value={contrasena}
                onChange={(e) => setContrasena(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>
          </>
        ) : null}

        {paso === 'inscripcion' && secreto ? (
          <>
            <Aviso tono="atencion">
              Esta cuenta todavía no tiene segundo factor. Registre la clave en su aplicación de
              autenticación (Google Authenticator, Aegis, 1Password) y confirme con el código que
              muestre. Esta clave no se vuelve a mostrar.
            </Aviso>

            <div className="campo">
              <label htmlFor="secreto">Clave para la aplicación</label>
              <input
                id="secreto"
                readOnly
                value={secreto.secreto}
                onFocus={(e) => e.currentTarget.select()}
                style={{ fontFamily: 'monospace', letterSpacing: '0.08em' }}
              />
            </div>

            <details style={{ marginBottom: 14, fontSize: 12.5, color: 'var(--texto-medio)' }}>
              <summary style={{ cursor: 'pointer' }}>Enlace otpauth:// completo</summary>
              <code style={{ wordBreak: 'break-all', display: 'block', marginTop: 8 }}>{secreto.uri}</code>
            </details>
          </>
        ) : null}

        {paso !== 'credenciales' ? (
          <div className="campo">
            <label htmlFor="codigo">Código de verificación</label>
            <input
              id="codigo"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={7}
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              placeholder="000000"
              required
              autoFocus
              style={{ fontFamily: 'monospace', letterSpacing: '0.3em' }}
            />
          </div>
        ) : null}

        <button
          type="submit"
          className="boton"
          style={{ width: '100%', justifyContent: 'center' }}
          disabled={enviando}
        >
          {enviando
            ? 'Verificando…'
            : paso === 'credenciales'
              ? 'Continuar'
              : paso === 'inscripcion'
                ? 'Confirmar e ingresar'
                : 'Ingresar'}
        </button>

        {paso !== 'credenciales' ? (
          <button
            type="button"
            className="boton secundario"
            style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}
            onClick={() => {
              setPaso('credenciales');
              setCodigo('');
              setSecreto(null);
              setError(null);
            }}
          >
            Volver
          </button>
        ) : null}
      </form>
    </div>
  );
}
